const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join( ', ' );

const scrollState = {
	htmlOverflow: '',
	bodyOverflow: '',
};

let activeModal = null;
let activeTrigger = null;

/**
 * Returns focusable nodes inside a container.
 *
 * @param {HTMLElement} container Container element.
 *
 * @return {HTMLElement[]} Focusable elements.
 */
const getFocusableElements = ( container ) => {

	if ( ! container ) {

		return [];

	}

	return [ ...container.querySelectorAll( FOCUSABLE_SELECTOR ) ].filter(
		( element ) => ! element.hasAttribute( 'hidden' ) && element.getAttribute( 'aria-hidden' ) !== 'true'
	);

};

/**
 * Locks page scrolling while modal is open.
 *
 * @return {void}
 */
const lockScroll = () => {

	scrollState.htmlOverflow = document.documentElement.style.overflow;
	scrollState.bodyOverflow = document.body.style.overflow;

	document.documentElement.style.overflow = 'hidden';
	document.body.style.overflow = 'hidden';

};

/**
 * Restores page scrolling.
 *
 * @return {void}
 */
const unlockScroll = () => {

	document.documentElement.style.overflow = scrollState.htmlOverflow;
	document.body.style.overflow = scrollState.bodyOverflow;

};

/**
 * Moves focus into modal.
 *
 * @param {HTMLElement} modal Modal element.
 *
 * @return {void}
 */
const focusModal = ( modal ) => {

	const focusableElements = getFocusableElements( modal );

	if ( focusableElements.length > 0 ) {

		focusableElements[0].focus();
		return;

	}

	const dialog = modal.querySelector( '.b-modal__dialog' );

	if ( dialog instanceof HTMLElement ) {

		dialog.focus();

	}

};

/**
 * Closes currently active modal.
 *
 * @param {boolean} restoreFocus Should focus return to trigger.
 *
 * @return {void}
 */
const closeActiveModal = ( restoreFocus = true ) => {

	if ( ! activeModal ) {

		return;

	}

	const closingModal = activeModal;
	const closingTrigger = activeTrigger;

	closingModal.setAttribute( 'aria-hidden', 'true' );
	closingModal.setAttribute( 'hidden', '' );
	closingModal.dispatchEvent( new CustomEvent( 'optery:modal-closed', {
		bubbles: true,
		detail: {
			modalId: closingModal.id || '',
			restoreFocus,
		},
	} ) );

	unlockScroll();

	if ( restoreFocus && closingTrigger instanceof HTMLElement ) {

		closingTrigger.focus();

	}

	activeTrigger = null;
	activeModal = null;

};

/**
 * Opens requested modal and closes previous one if needed.
 *
 * @param {HTMLElement} modal Modal element.
 * @param {HTMLElement|null} trigger Trigger element.
 *
 * @return {void}
 */
const openModal = ( modal, trigger = null ) => {

	if ( activeModal === modal ) {

		return;

	}

	if ( activeModal ) {

		closeActiveModal( false );

	}

	activeModal = modal;
	activeTrigger = trigger;

	modal.removeAttribute( 'hidden' );
	modal.setAttribute( 'aria-hidden', 'false' );

	lockScroll();
	focusModal( modal );

};

/**
 * Traps keyboard focus in active modal.
 *
 * @param {KeyboardEvent} event Keyboard event.
 *
 * @return {void}
 */
const trapFocus = ( event ) => {

	if ( ! activeModal || event.key !== 'Tab' ) {

		return;

	}

	const focusableElements = getFocusableElements( activeModal );

	if ( focusableElements.length === 0 ) {

		event.preventDefault();
		focusModal( activeModal );
		return;

	}

	const firstElement = focusableElements[0];
	const lastElement = focusableElements[focusableElements.length - 1];
	const currentElement = document.activeElement;

	if ( event.shiftKey && currentElement === firstElement ) {

		event.preventDefault();
		lastElement.focus();
		return;

	}

	if ( ! event.shiftKey && currentElement === lastElement ) {

		event.preventDefault();
		firstElement.focus();

	}

};

document.addEventListener( 'click', ( event ) => {

	const trigger = event.target.closest( '[data-modal-trigger]' );

	if ( trigger ) {

		const modalId = trigger.getAttribute( 'data-modal-trigger' )?.trim();
		const modal = modalId ? document.getElementById( modalId ) : null;

		if ( ! ( modal instanceof HTMLElement ) || ! modal.hasAttribute( 'data-modal' ) ) {

			return;

		}

		event.preventDefault();
		openModal( modal, trigger );
		return;

	}

	if ( ! activeModal ) {

		return;

	}

	const closeButton = event.target.closest( '[data-modal-close]' );

	if ( closeButton ) {

		event.preventDefault();
		closeActiveModal();
		return;

	}

	if ( event.target === activeModal ) {

		closeActiveModal();

	}

} );

document.addEventListener( 'keydown', ( event ) => {

	if ( ! activeModal ) {

		return;

	}

	if ( event.key === 'Escape' ) {

		event.preventDefault();
		closeActiveModal();
		return;

	}

	trapFocus( event );

} );
